/**
 * Shared client fetch + cache for listing enrichment.
 * Used by SectionListingSeoCopy and SectionListingNearbyLinks.
 */

import { sameOriginApiBase } from './sameOriginApiBase';
import { createSlug } from './urlHelpers';

const lookupCache = new Map();

const clean = value => String(value || '').trim();

const performerIdsFromPublicData = pd => {
  const ids = [];
  const push = value => {
    if (value == null || value === '') return;
    if (Array.isArray(value)) {
      value.forEach(push);
      return;
    }
    if (typeof value === 'object') {
      push(value.id ?? value.performerId ?? value.ticketNetworkPerformerId ?? value.tnPerformerId);
      return;
    }
    String(value)
      .split(',')
      .map(s => s.trim())
      .filter(Boolean)
      .forEach(id => {
        if (!ids.includes(id)) ids.push(id);
      });
  };
  push(pd.ticketNetworkPerformerId);
  push(pd.tnPerformerId);
  push(pd.performerIds);
  push(pd.ticketNetworkPerformerIds);
  push(pd.tnPerformerIds);
  return ids.length ? ids.join(',') : '';
};

export const enrichmentParamsFromPublicData = publicData => {
  const pd = publicData || {};
  const loc = pd.location || {};
  const venueName = clean(pd.venueName) || clean(pd.venue) || null;
  const venueId = pd.ticketNetworkVenueId;
  const city = clean(pd.venueCity) || clean(loc.city) || clean(loc.addressLocality);
  const state = clean(pd.venueState) || clean(loc.state) || clean(loc.addressRegion);
  const performers = clean(pd.headlinersAndArtists) || clean(pd.performerName) || '';
  const performerIds = performerIdsFromPublicData(pd);
  const category = clean(pd.category) || clean(pd.eventCategory) || '';

  if (!venueName && !venueId && !city && !performers && !performerIds) {
    return null;
  }

  return {
    ...(venueId ? { venueId: String(venueId) } : {}),
    ...(venueName ? { name: venueName, slug: createSlug(venueName) } : {}),
    ...(city ? { city } : {}),
    ...(state ? { state } : {}),
    ...(performers ? { performers } : {}),
    ...(performerIds ? { performerIds } : {}),
    ...(category ? { category } : {}),
  };
};

export const getCachedListingEnrichment = cacheKey =>
  cacheKey && lookupCache.has(cacheKey) ? lookupCache.get(cacheKey) : null;

export const setCachedListingEnrichment = (cacheKey, value) => {
  if (cacheKey) {
    lookupCache.set(cacheKey, value);
  }
};

export const fetchListingEnrichment = params => {
  const base = sameOriginApiBase();
  if (!base || !params) {
    return Promise.resolve(null);
  }
  const query = new URLSearchParams(params).toString();
  return fetch(`${base}/api/seo-discovery/listing-enrichment?${query}`, {
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
  })
    .then(res => (res.ok ? res.json() : null))
    .then(
      body =>
        body || {
          venue: null,
          city: null,
          performers: [],
          relationships: null,
        }
    )
    .catch(() => ({
      venue: null,
      city: null,
      performers: [],
      relationships: null,
    }));
};
